import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, PaperPlaneRight, ArrowsClockwise, PencilSimple, Check, LinkSimple, Eye, Sparkle, Robot, ShieldCheck,
} from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import { useThreadStream } from "@/lib/useThreadStream";

function InboundBubble({ msg, clientName }) {
  return (
    <div className="flex gap-3 mb-4 animate-fade-in" data-testid="msg-inbound">
      <div className="w-8 h-8 rounded-full bg-[color:var(--oz-hover)] border border-[color:var(--oz-border-strong)] flex items-center justify-center shrink-0 font-mono text-xs text-[color:var(--oz-text-dim)]">
        {clientName?.[0] || "?"}
      </div>
      <div className="flex-1">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-xs font-medium text-[color:var(--oz-text)]">{clientName}</span>
          <span className="text-[10px] font-mono text-[color:var(--oz-text-muted)]">
            {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
        <div className="text-sm text-[color:var(--oz-text)] bg-[color:var(--oz-hover)] border border-[color:var(--oz-border)] rounded-sm p-3 max-w-2xl whitespace-pre-wrap">
          {msg.content}
        </div>
      </div>
    </div>
  );
}

function DraftBubble({ msg, onSend, onRegenerate, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.content);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft(msg.content); }, [msg.content]);

  const save = async () => {
    setBusy(true);
    await onEdit(msg.id, draft);
    setEditing(false);
    setBusy(false);
  };

  const send = async () => {
    setBusy(true);
    if (editing && draft !== msg.content) await onEdit(msg.id, draft);
    await onSend(msg.id);
    setBusy(false);
  };

  const regen = async () => {
    setBusy(true);
    await onRegenerate(msg.id);
    setBusy(false);
  };

  const sent = msg.status === "sent";
  const autoSent = sent && msg.auto_sent;
  const verdict = msg.auto_send_verdict;

  const borderClass = autoSent
    ? "border-[color:var(--oz-emerald)] bg-[color:var(--oz-emerald)]/15"
    : sent
    ? "border-[color:var(--oz-emerald)] bg-[color:var(--oz-emerald)]/10"
    : "border-[color:var(--oz-gold)] bg-[color:var(--oz-gold)]/5";

  const iconBg = autoSent ? "var(--oz-emerald)" : sent ? "var(--oz-emerald)" : "var(--oz-gold)";
  const IconCmp = autoSent ? Robot : Eye;

  const label = autoSent
    ? "AUTO-SENT AS MARK · RULES PASSED"
    : sent
    ? "Sent as Mark"
    : "AI-drafted · awaiting approval";

  return (
    <div className="flex flex-row-reverse gap-3 mb-4 animate-fade-in" data-testid={sent ? "msg-sent" : "msg-draft"}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: iconBg }}>
        <IconCmp size={14} weight="fill" color="#000" />
      </div>
      <div className="flex-1 flex flex-col items-end">
        <div className="flex items-baseline gap-2 mb-1">
          {!sent && <Sparkle size={10} color="var(--oz-gold)" weight="fill" />}
          {autoSent && <Robot size={10} color="var(--oz-emerald)" weight="fill" />}
          <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: autoSent || sent ? "var(--oz-emerald)" : "var(--oz-gold)" }}>
            {label}
          </span>
          <span className="text-[10px] font-mono text-[color:var(--oz-text-muted)]">
            {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
        <div className={`text-sm p-3 max-w-2xl whitespace-pre-wrap rounded-sm border text-[color:var(--oz-text)] ${borderClass}`}>
          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={Math.min(10, Math.max(3, draft.split("\n").length))}
              className="w-full bg-transparent outline-none resize-none min-w-[280px]"
              autoFocus
              data-testid="edit-draft-textarea"
            />
          ) : (
            <div>{msg.content}</div>
          )}
        </div>
        {!sent && verdict?.reasons?.length > 0 && (
          <div className="mt-1 text-[10px] font-mono text-[color:var(--oz-amber)] max-w-md text-right" data-testid="verdict-reasons">
            Held: {verdict.reasons.slice(0, 2).join(" · ")}
          </div>
        )}
        {!sent && (
          <div className="flex gap-1 mt-2">
            {editing ? (
              <>
                <button onClick={save} disabled={busy} className="btn-primary text-xs px-2 py-1 flex items-center gap-1" data-testid="save-edit-btn">
                  <Check size={11} /> Save
                </button>
                <button onClick={() => { setEditing(false); setDraft(msg.content); }} className="btn-ghost text-xs px-2 py-1">Cancel</button>
              </>
            ) : (
              <>
                <button onClick={send} disabled={busy} className="btn-primary text-xs px-2 py-1 flex items-center gap-1" data-testid={`send-draft-${msg.id}`}>
                  <PaperPlaneRight size={11} /> Send as Mark
                </button>
                <button onClick={() => setEditing(true)} className="btn-ghost text-xs px-2 py-1 flex items-center gap-1" data-testid={`edit-draft-${msg.id}`}>
                  <PencilSimple size={11} /> Edit
                </button>
                <button onClick={regen} disabled={busy} className="btn-ghost text-xs px-2 py-1 flex items-center gap-1" data-testid={`regen-draft-${msg.id}`}>
                  <ArrowsClockwise size={11} /> Regenerate
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Relay() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [current, setCurrent] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newT, setNewT] = useState({ client_name: "", client_org: "", client_email: "", context: "" });
  const [inbound, setInbound] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get("/relay/threads");
    setThreads(data);
    if (!threadId && data.length) navigate(`/relay/${data[0].id}`, { replace: true });
  };

  const loadThread = async (id) => {
    const { data } = await api.get(`/relay/threads/${id}`);
    setCurrent(data);
    setTimeout(() => scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 100);
  };

  const [rulesEnabled, setRulesEnabled] = useState(false);
  const loadRules = async () => {
    try { const { data } = await api.get("/relay/rules"); setRulesEnabled(!!data.enabled); } catch {}
  };

  useEffect(() => { loadThreads(); loadRules(); }, []);
  useEffect(() => { if (threadId) loadThread(threadId); }, [threadId]);

  // Live SSE — merge incoming/updated messages into current thread
  useThreadStream(threadId, (evt) => {
    if (!evt?.payload) return;
    const msg = evt.payload;
    setCurrent((prev) => {
      if (!prev || prev.thread.id !== msg.thread_id) return prev;
      const idx = prev.messages.findIndex((m) => m.id === msg.id);
      let messages;
      if (evt.kind === "message_updated" && idx >= 0) {
        messages = prev.messages.map((m) => (m.id === msg.id ? { ...m, ...msg } : m));
      } else if (idx === -1) {
        messages = [...prev.messages, msg];
      } else {
        messages = prev.messages;
      }
      return { ...prev, messages };
    });
    setTimeout(() => scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  }, /*isPublic=*/ false);

  const createThread = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/relay/threads", newT);
    toast.success(`Thread with ${data.client_name} opened`);
    setShowNew(false);
    setNewT({ client_name: "", client_org: "", client_email: "", context: "" });
    await loadThreads();
    navigate(`/relay/${data.id}`);
  };

  const simulateInbound = async (e) => {
    e.preventDefault();
    if (!inbound.trim() || !threadId) return;
    setBusy(true);
    try {
      await api.post(`/relay/threads/${threadId}/inbound`, { content: inbound });
      setInbound("");
      await loadThread(threadId);
      toast.success("Inbound received · AI drafting reply");
    } catch { toast.error("Failed to send"); }
    finally { setBusy(false); }
  };

  const sendDraft = async (id) => {
    await api.post(`/relay/messages/${id}/send`);
    toast.success("Sent as Mark. Client sees it now.");
    loadThread(threadId);
  };

  const editDraft = async (id, content) => {
    await api.patch(`/relay/messages/${id}`, { content });
    loadThread(threadId);
  };

  const regen = async (id) => {
    await api.post(`/relay/messages/${id}/regenerate`);
    toast.success("New draft generated");
    loadThread(threadId);
  };

  const copyLink = () => {
    const url = `${window.location.origin}/c/${threadId}`;
    navigator.clipboard.writeText(url);
    toast.success("Client magic link copied");
  };

  const openClientView = () => {
    window.open(`${window.location.origin}/c/${threadId}`, "_blank");
  };

  const messages = current?.messages || [];
  const clientName = current?.thread?.client_name;

  return (
    <div className="flex h-screen">
      {/* Thread list */}
      <div className="w-72 border-r border-[color:var(--oz-border)] flex flex-col shrink-0" style={{ background: "var(--oz-card)" }}>
        <div className="p-4 border-b border-[color:var(--oz-border)] flex items-center justify-between">
          <div>
            <div className="overline">The Curtain</div>
            <div className="font-display text-sm">Client Threads</div>
          </div>
          <button data-testid="new-thread-btn" onClick={() => setShowNew(true)} className="w-7 h-7 flex items-center justify-center border border-[color:var(--oz-border-strong)] hover:border-[color:var(--oz-emerald)] rounded-sm transition-colors">
            <Plus size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.map((t) => (
            <button
              key={t.id}
              data-testid={`thread-item-${t.id}`}
              onClick={() => navigate(`/relay/${t.id}`)}
              className={`w-full text-left p-3 border-b border-[color:var(--oz-border)] transition-colors ${
                t.id === threadId ? "bg-[color:var(--oz-hover)] border-l-2 border-l-[color:var(--oz-gold)]" : "hover:bg-[color:var(--oz-hover)] border-l-2 border-l-transparent"
              }`}
            >
              <div className="flex items-center justify-between mb-0.5">
                <div className="text-sm font-medium truncate">{t.client_name}</div>
                {t.unread > 0 && <span className="text-[9px] font-mono px-1 py-0.5" style={{ background: "var(--oz-gold)", color: "#000" }}>{t.unread}</span>}
              </div>
              <div className="text-[11px] text-[color:var(--oz-text-muted)] truncate">{t.client_org}</div>
            </button>
          ))}
          {threads.length === 0 && <div className="p-6 text-center text-xs text-[color:var(--oz-text-muted)]">No threads yet. Create one to start.</div>}
        </div>
      </div>

      {/* Chat panel — glassmorphic curtain */}
      <div className="flex-1 flex flex-col oz-glass" style={{ background: "rgba(6, 12, 9, 0.5)" }}>
        {showNew ? (
          <div className="p-8 max-w-lg">
            <PageHeader overline="New client thread" title="Open the Curtain" subtitle="Add context so the AI can speak in your voice." />
            <form onSubmit={createThread} className="space-y-3">
              <input required placeholder="Client name" value={newT.client_name} onChange={(e) => setNewT({ ...newT, client_name: e.target.value })} className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" data-testid="new-thread-name" />
              <input placeholder="Organization" value={newT.client_org} onChange={(e) => setNewT({ ...newT, client_org: e.target.value })} className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
              <input placeholder="Email" value={newT.client_email} onChange={(e) => setNewT({ ...newT, client_email: e.target.value })} className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
              <textarea placeholder="Context — who they are, prior history, what to know" value={newT.context} onChange={(e) => setNewT({ ...newT, context: e.target.value })} rows="4" className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
              <div className="flex gap-2">
                <button className="btn-primary text-sm" data-testid="save-thread-btn">Open thread</button>
                <button type="button" onClick={() => setShowNew(false)} className="btn-ghost text-sm">Cancel</button>
              </div>
            </form>
          </div>
        ) : !current ? (
          <div className="flex-1 flex items-center justify-center text-[color:var(--oz-text-muted)]">Select a thread</div>
        ) : (
          <>
            {/* Header */}
            <div className="border-b border-white/5 p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-display text-lg">{current.thread.client_name}</div>
                  <span className="chip chip-gold text-[9px]">BEHIND THE CURTAIN</span>
                  {rulesEnabled ? (
                    <span className="chip chip-green text-[9px] flex items-center gap-1" data-testid="auto-approve-on">
                      <ShieldCheck size={10} weight="fill" /> AUTO-APPROVE ON
                    </span>
                  ) : (
                    <button onClick={() => navigate("/rules")} className="chip text-[9px] hover:border-[color:var(--oz-emerald)]" data-testid="auto-approve-off">
                      auto-approve off
                    </button>
                  )}
                </div>
                <div className="text-xs text-[color:var(--oz-text-dim)]">{current.thread.client_org} · {current.thread.client_email || "no email"}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={copyLink} data-testid="copy-link-btn" className="btn-ghost text-xs flex items-center gap-1"><LinkSimple size={12} /> Copy client link</button>
                <button onClick={openClientView} data-testid="open-client-view-btn" className="btn-ghost text-xs flex items-center gap-1"><Eye size={12} /> Client view</button>
              </div>
            </div>

            {current.thread.context && (
              <div className="border-b border-white/5 px-4 py-2 text-[11px] text-[color:var(--oz-text-muted)] font-mono">
                Context: {current.thread.context}
              </div>
            )}

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6" data-testid="messages-scroll">
              {messages.length === 0 && (
                <div className="text-center text-sm text-[color:var(--oz-text-muted)] mt-10">
                  No messages yet. Simulate an inbound below to see the AI draft in your voice.
                </div>
              )}
              {messages.map((m) =>
                m.direction === "inbound"
                  ? <InboundBubble key={m.id} msg={m} clientName={clientName} />
                  : <DraftBubble key={m.id} msg={m} onSend={sendDraft} onEdit={editDraft} onRegenerate={regen} />
              )}
            </div>

            {/* Simulate inbound (operator side — pretend to be the client) */}
            <form onSubmit={simulateInbound} className="border-t border-white/5 p-4 flex gap-2">
              <input
                data-testid="simulate-inbound-input"
                value={inbound}
                onChange={(e) => setInbound(e.target.value)}
                placeholder={`Simulate a message from ${clientName || "the client"}…`}
                className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm focus:border-[color:var(--oz-gold)] outline-none"
              />
              <button disabled={busy || !inbound.trim()} className="btn-primary text-sm flex items-center gap-1" data-testid="simulate-inbound-btn">
                {busy ? "Drafting…" : <>Receive & Draft <Sparkle size={12} weight="fill" /></>}
              </button>
            </form>
            <div className="px-4 pb-3 text-[10px] text-[color:var(--oz-text-muted)] font-mono">
              💡 Pro tip: Share the "client magic link" so real clients can chat here. Every message they send triggers a Claude-drafted reply in Mark's voice, awaiting your approval.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
